import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { canManageInstitutionCampaigns } from '@/lib/campaign-totp-step-up'
import {
  formatCompactDate,
  formatCurrency,
  formatNumber,
  getStatusLabel,
} from '@/lib/campaign-dashboard'
import type {
  CampaignDashboardCampaign,
  CampaignDashboardSummary,
} from '@/types/campaign-report'
import styles from './CampaignDashboard.module.css'

interface CampaignDashboardHeaderProps {
  title: string
  campaign: CampaignDashboardCampaign
  summary: CampaignDashboardSummary
  onCopyPublicUrl: () => void
}

function getStatusClass(status: CampaignDashboardCampaign['status']): string {
  switch (status) {
    case 'active':
      return styles.statusActive
    case 'draft':
      return styles.statusDraft
    case 'finished':
      return styles.statusFinished
    case 'cancelled':
      return styles.statusCancelled
    default:
      return styles.statusDraft
  }
}

export function CampaignDashboardHeader({
  title,
  campaign,
  summary,
  onCopyPublicUrl,
}: CampaignDashboardHeaderProps) {
  const { guard, isCollaborator } = useAuth()
  const canManage = canManageInstitutionCampaigns(guard, Boolean(isCollaborator))
  const publicUrl = campaign.slug ? `/campanha/${campaign.slug}` : null

  return (
    <section className={styles.heroCard}>
      <div className={styles.heroHeader}>
        <div className={styles.heroTitleWrap}>
          <span className={styles.eyebrow}>
            <Icon icon="mdi:chart-box-outline" />
            Dashboard da campanha
          </span>

          <h1 className={styles.heroTitle}>{title}</h1>

          <div className={styles.statusRow}>
            <span className={`${styles.statusBadge} ${getStatusClass(campaign.status)}`}>
              <Icon icon="mdi:bullhorn" />
              {getStatusLabel(campaign.status)}
            </span>
            <span className={styles.metaPill}>
              <Icon icon="mdi:calendar-range" />
              {formatCompactDate(campaign.starts_at)} ate {formatCompactDate(campaign.ends_at)}
            </span>
            <span className={styles.metaPill}>
              <Icon icon="mdi:numeric" />
              {formatNumber(campaign.total_numbers)} numeros
            </span>
            <span className={styles.metaPill}>
              <Icon icon="mdi:cash" />
              {formatCurrency(campaign.price)} por numero
            </span>
            <span className={styles.metaPill}>
              <Icon icon="mdi:chart-donut" />
              {summary.sell_through_percent !== null
                ? `${summary.sell_through_percent}% vendido`
                : 'Sem ocupacao calculada'}
            </span>
          </div>
        </div>

        <div className={styles.heroActions}>
          {publicUrl && (
            <a
              href={publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.actionLink}
            >
              <Icon icon="mdi:open-in-new" />
              Abrir landing
            </a>
          )}

          {canManage ? (
            <Link
              to={`/campanhas/editar?id=${campaign.id}`}
              className={styles.actionLink}
            >
              <Icon icon="mdi:pencil-outline" />
              Editar campanha
            </Link>
          ) : null}

          <button
            type="button"
            className={styles.actionButton}
            onClick={onCopyPublicUrl}
            disabled={!publicUrl}
          >
            <Icon icon="mdi:content-copy" />
            Copiar URL publica
          </button>
        </div>
      </div>
    </section>
  )
}
